import * as PersonnelDetailActions from './+state/personnel-detail.actions';
import * as PersonnelDetailSelectors from './+state/personnel-detail.selectors';

import {
	Component,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
} from '@angular/core';
import { FormBuilder, FormControlName, FormGroup } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { map, takeUntil } from 'rxjs/operators';

import { FieldWorkerPayrollType } from 'src/shared/api';
import { FormConfig } from 'src/shared/form-models/form-cva.component';
import { PersonnelDetail } from './personnel-detail.models';
import { PersonnelDetailPartialState } from './+state/personnel-detail.reducer';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import { Subject } from 'rxjs';
import { fieldWorkerToPersonnelForm } from './personnel-detail.utility';
import { preventFlicker } from 'src/shared/utilities/custom-rxjs-operators';
import { wrapChildControls } from 'src/shared/utilities/utility-functions';

const personnelFormConfig: FormConfig<PersonnelDetail> = {
	generalInfo: [''],
	company: [''],
	unavailableDays: [''],
};

@Component({
	templateUrl: './personnel-detail.component.html',
})
export class PersonnelDetailComponent
	extends SelfDestruct
	implements OnInit, OnDestroy {
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;

	private _invalidSubmission = new Subject<number>();
	form = this.fb.group(personnelFormConfig) as FormGroup;

	readonly id$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getSelectedPersonnelId)
	);

	readonly title$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getTitle)
	);

	readonly isNewRecord$ = this.id$.pipe(map((id) => id === 0));

	readonly isLoading$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getLoadingStatus)
	);

	readonly isSaving$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getIsSaving),
		preventFlicker(1000)
	);

	readonly is1099Employee$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getIs1099)
	);

	readonly isFieldSupport$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getIsFieldSupport)
	);

	readonly invalidSubmission$ = this._invalidSubmission.asObservable();

	readonly jobAssignments$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getAssignments)
	);

	readonly historyListMessage$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getHistoryListMessage)
	);

	readonly isHistoryLoading$ = this.personnelStore.pipe(
		select(PersonnelDetailSelectors.getIsHistoryLoading)
	);

	readonly history$ = this.personnelStore.pipe(select(PersonnelDetailSelectors.getHistory));

	constructor(
		private readonly personnelStore: Store<PersonnelDetailPartialState>,
		private readonly fb: FormBuilder
	) {
		super();
	}

	ngOnInit() {
		this.personnelStore
			.pipe(
				select(PersonnelDetailSelectors.getSelectedPersonnel),
				map(fieldWorkerToPersonnelForm),
				takeUntil(this.destroyed)
			)
			.subscribe((formValue) =>
				this.form.patchValue(formValue, { emitEvent: false })
			);
		this.is1099Employee$
			.pipe(takeUntil(this.destroyed))
			.subscribe((is1099) => {
				if (is1099) {
					this.form.get('company').enable();
				} else {
					this.form.get('company').disable();
				}
			});
	}

	save(stay = false) {
		if (this.form.valid) {
			const formValue = this.form.value;
			this.personnelStore.dispatch(
				PersonnelDetailActions.saveRequested({
					shouldStayOnPage: stay,
					formValue,
				})
			);
		} else {
			const controls = wrapChildControls(this.childControls);
			controls.forEach((c) => c.markAllAsTouched());
			this._invalidSubmission.next(new Date().getTime());
		}
	}

	payrollTypeSelected(payrollType: FieldWorkerPayrollType): void {
		this.personnelStore.dispatch(
			PersonnelDetailActions.payrollTypeUpdated({ payrollType })
		);
	}

	fieldWorkerTypeSelected(fieldWorkerTypeId: number): void {
		this.personnelStore.dispatch(
			PersonnelDetailActions.fieldWorkerTypeUpdated({ fieldWorkerTypeId })
		);
	}

	ngOnDestroy() {
		this.personnelStore.dispatch(
			PersonnelDetailActions.personnelDetailDestroyed()
		);
		super.ngOnDestroy();
	}
}
